/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { Alert, AlertColor, Snackbar } from "@mui/material";
import { IGameConfigBranchInfo, useGameConfigService, IStaticDataFileMapping } from "services/backend/GameConfigService";
import { AppContext } from "utils/AppContext";
import { StringInputPopup } from "components/StringInputPopup";
import { LoadingOverlay } from "components/LoadingOverlay";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { tabProps, TabPanelContainer } from "components/TabPanelContainer";
import { BranchStateTabPanel } from "./BranchStateTabPanel";
import { PullRequestData } from "../../services/backend/DataSchemaService";
import { YesNoCancelPopup } from "../../components/YesNoCancelPopup";
import { WadTemplatesTabPanel } from "./WadTemplatesTabPanel";
import { ImportDataTabPanel } from "./ImportDataTabPanel";
import { ItemsManagementTabPanel } from "./ItemsManagementTabPanel";
import { DeployDataTabPanel } from "./DeployDataTabPanel";

const GameConfigEditor = () => {
  const gameConfigService = useGameConfigService();

  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [branches, setBranches] = useState<string[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string | undefined>(undefined);
  const [createBranchFormVisible, setCreateBranchFormVisible] = useState<boolean>(false);
  const [branchInfo, setBranchInfo] = useState<IGameConfigBranchInfo | undefined>(undefined);
  const [submitBranchFormVisible, setSubmitBranchFormVisible] = useState<boolean>(false);
  const [deleteBranchPopupVisible, setDeleteBranchPopupVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [commitFormVisible, setCommitFormVisible] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const [remainingIds, setRemainingIds] = useState<IStaticDataFileMapping[] | undefined>(undefined);
  const [importOutput, setImportOutput] = useState<string | undefined>(undefined);
  const [deployOutput, setDeployOutput] = useState<string | undefined>(undefined);

  const appContext = useContext(AppContext);

  const fetchBranches = async () => {
    try {
      setIsLoading(true);
      const response = await gameConfigService.listBranches();
      setBranches(response.list || []);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of game config branches.");
      setAlertSeverity("error");
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadBranch = async (branch: string) => {
    try {
      setIsLoading(true);
      const branchInfo = await gameConfigService.getBranchInfo(branch);
      setBranchInfo({ ...branchInfo, branch });
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not load branch information.");
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (appContext?.selectedEnvironment) {
      (async () => fetchBranches())();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext?.selectedEnvironment]);

  useEffect(() => {
    appContext?.setTitle("Game Config Editor");
    appContext?.setTargetAPI("environment");
  }, [appContext]);

  useEffect(() => {
    if (branches.length > 0 && selectedBranch !== undefined) {
      (async () => loadBranch(selectedBranch))();
    }
    if (!selectedBranch) {
      setBranchInfo(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, selectedBranch]);

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const selectBranchMenuClickHandler = async (name: string) => {
    try {
      setIsLoading(true);
      await gameConfigService.selectBranch(name);
      setSelectedBranch(name);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Could not checkout branch "${name}".`);
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }
  };

  const createBranchButtonClickHandler = () => {
    setCreateBranchFormVisible(true);
  };

  const createBranchFormDismissHandler = () => {
    setCreateBranchFormVisible(false);
  };

  const createBranchFormAcceptHandler = async (name: string) => {
    try {
      setIsLoading(true);
      await gameConfigService.createBranch(name);
      await fetchBranches();
      setSelectedBranch(name);
      setAlertMessage(`Branch "${name}" created successfully.`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Could not create branch "${name}".`);
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
      setCreateBranchFormVisible(false);
    }
  };

  const deleteBranchButtonClickHandler = () => {
    setDeleteBranchPopupVisible(true);
  };

  const deleteBranchPopupNoHandler = () => {
    setDeleteBranchPopupVisible(false);
  };

  const deleteBranchPopupYesHandler = async () => {
    if (!selectedBranch) {
      return;
    }

    try {
      setIsLoading(true);
      await gameConfigService.deleteBranch(selectedBranch);
      await fetchBranches();
      setSelectedBranch(undefined);
      setAlertMessage(`Branch "${selectedBranch}" deleted.`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Could not delete branch "${selectedBranch}".`);
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
      setDeleteBranchPopupVisible(false);
    }
  };

  const submitBranchButtonClickHandler = () => {
    setSubmitBranchFormVisible(true);
  };

  const submitBranchFormDismissHandler = () => {
    setSubmitBranchFormVisible(false);
  };

  const submitBranchFormAcceptHandler = async (prTitle: string) => {
    if (!selectedBranch) {
      return;
    }
    try {
      setIsLoading(true);
      await gameConfigService.submitBranch(selectedBranch, prTitle);
      await loadBranch(selectedBranch);
      setAlertMessage(`Branch "${selectedBranch}" submitted for review.`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Could not submit branch "${selectedBranch}".`);
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
      setSubmitBranchFormVisible(false);
    }
  };

  const navigateToPullRequest = (pr: PullRequestData | undefined) => {
    if (pr) {
      window.open(`https://github.com/FortisGames/proj-townsfolk-game-platform/pull/${pr.number}`);
    }
  };

  const commitBranchButtonClickHandler = () => {
    setCommitFormVisible(true);
  };

  const commitBranchFormDismissHandler = () => {
    setCommitFormVisible(false);
  };

  const commitBranchFormAcceptHandler = async (message: string) => {
    if (!selectedBranch) {
      return;
    }

    try {
      setIsLoading(true);
      await gameConfigService.commitBranch(selectedBranch, message);
      await loadBranch(selectedBranch);
      setAlertMessage(`Changes committed to branch "${selectedBranch}".`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Could not commit changes to branch "${selectedBranch}".`);
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
      setCommitFormVisible(false);
    }
  };

  const revertBranchButtonClickHandler = async () => {
    if (!selectedBranch) {
      return;
    }

    try {
      setIsLoading(true);
      await gameConfigService.revertBranch(selectedBranch);
      await loadBranch(selectedBranch);
      setAlertMessage(`Changes reverted in branch "${selectedBranch}".`);
      setAlertSeverity("success");
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Could not revert changes in branch "${selectedBranch}".`);
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Branch state" {...tabProps(0)} />
          <Tab label="Items management" {...tabProps(1)} />
          <Tab label="Wad Templates" {...tabProps(2)} />
          <Tab label="Import data" {...tabProps(3)} />
          <Tab label="Deploy data" {...tabProps(4)} />
        </Tabs>
      </Box>
      <TabPanelContainer value={tabValue} index={0}>
        <BranchStateTabPanel
          branches={branches}
          branchInfo={branchInfo}
          selectedBranch={selectedBranch}
          setSelectedBranch={selectBranchMenuClickHandler}
          createBranchButtonClickHandler={createBranchButtonClickHandler}
          commitBranchButtonClickHandler={commitBranchButtonClickHandler}
          revertBranchButtonClickHandler={revertBranchButtonClickHandler}
          submitBranchButtonClickHandler={submitBranchButtonClickHandler}
          mergeBranchButtonClickHandler={() => navigateToPullRequest(branchInfo?.pullRequest)}
          deleteBranchButtonClickHandler={deleteBranchButtonClickHandler}
          onSwitchToImportTab={() => setTabValue(3)}
          onRemainingIdsUpdate={setRemainingIds}
          onImportOutputUpdate={setImportOutput}
          onDeployOutputUpdate={setDeployOutput}
        />
      </TabPanelContainer>
      <TabPanelContainer value={tabValue} index={1}>
        <ItemsManagementTabPanel selectedBranch={selectedBranch} />
      </TabPanelContainer>
      <TabPanelContainer value={tabValue} index={2}>
        <WadTemplatesTabPanel selectedBranch={selectedBranch} />
      </TabPanelContainer>
      <TabPanelContainer value={tabValue} index={3}>
        <ImportDataTabPanel
          selectedBranch={selectedBranch}
          initialRemainingIds={remainingIds}
          toolboxOutput={importOutput}
          onToolboxOutputUpdate={setImportOutput}
        />
      </TabPanelContainer>
      <TabPanelContainer value={tabValue} index={4}>
        <DeployDataTabPanel
          selectedBranch={selectedBranch}
          toolboxOutput={deployOutput}
          onToolboxOutputUpdate={setDeployOutput}
        />
      </TabPanelContainer>
      <StringInputPopup
        visible={createBranchFormVisible}
        title="New Game Config Branch"
        message="Please enter a name for the branch:"
        onDismiss={createBranchFormDismissHandler}
        onAccept={createBranchFormAcceptHandler}
      />
      <StringInputPopup
        visible={submitBranchFormVisible}
        title="Submit Game Config Branch"
        message="Please enter a PR title for this submission:"
        onDismiss={submitBranchFormDismissHandler}
        onAccept={submitBranchFormAcceptHandler}
      />
      <StringInputPopup
        visible={commitFormVisible}
        title="Commit Changes"
        message="Please enter a commit message:"
        onDismiss={commitBranchFormDismissHandler}
        onAccept={commitBranchFormAcceptHandler}
      />
      <YesNoCancelPopup
        visible={deleteBranchPopupVisible}
        title={"Delete Branch"}
        message={`Are you sure you want to delete the branch named <b>${selectedBranch}</b>?<br/>`}
        onNo={deleteBranchPopupNoHandler}
        onYes={deleteBranchPopupYesHandler}
      />
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
      {isLoading && <LoadingOverlay />}
    </>
  );
};

export default GameConfigEditor;
